import { tipoVisita, lugarVisita } from '../lib/formato';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CabeceraPagina } from '../components/Armazon';
import { turnoDe } from '../components/Pase';
import { Aviso, Boton, Cargando, Pildora, Tarjeta, Vacio } from '../components/ui';
import { api, qs } from '../lib/api';
import { ESTADO_ETIQUETA, fecha, franja } from '../lib/formato';
import { useDatos, useSesion } from '../lib/sesion';
import type { Estado, Pagina, SolicitudResumen } from '../lib/tipos';

const ESTADOS: Estado[] = ['DRAFT', 'SENT', 'RECEIVED', 'AUTHORIZED', 'REJECTED', 'CANCELLED','PENDING_REVIEW','PENDING_FOLLOWUP','CLOSED','NOT_PERFORMED'];

export function Solicitudes() {
  const navegar = useNavigate();
  const { es } = useSesion();
  const [params, setParams] = useSearchParams();
  const estado = params.get('estado') ?? '';
  const [busqueda, setBusqueda] = useState(params.get('q') ?? '');
  const q = params.get('q') ?? '';
  const pagina = Number(params.get('pagina') ?? 1);

  const { datos, cargando, error } = useDatos(
    (s) =>
      api.get<Pagina<SolicitudResumen>>(
        `/requests${qs({ status: estado || undefined, q: q || undefined, page: pagina, pageSize: 25 })}`,
        s,
      ),
    [estado, q, pagina],
  );

  const cambiar = (clave: string, valor: string) => {
    const p = new URLSearchParams(params);
    if (valor) p.set(clave, valor);
    else p.delete(clave);
    if (clave !== 'pagina') p.delete('pagina');
    setParams(p, { replace: true });
  };

  const total = datos?.total ?? 0;
  const paginas = Math.max(1, Math.ceil(total / (datos?.pageSize ?? 25)));

  return (
    <>
      <CabeceraPagina
        titulo="Solicitudes"
        acciones={
          es('SALESPERSON', 'ADMIN') ? (
            <Boton variante="clave" onClick={() => navegar('/solicitudes/nueva')}>
              Nueva solicitud
            </Boton>
          ) : null
        }
      />

      <div className="pila">
        <div className="fila" style={{ gap: 8 }}>
          <div className="selector-vista">
            <button aria-pressed={!estado} onClick={() => cambiar('estado', '')}>
              Todas
            </button>
            {ESTADOS.map((e) => (
              <button key={e} aria-pressed={estado === e} onClick={() => cambiar('estado', e)}>
                {ESTADO_ETIQUETA[e]}
              </button>
            ))}
          </div>

          <form
            style={{ marginLeft: 'auto', display: 'flex', gap: 8, minWidth: 260 }}
            onSubmit={(ev) => {
              ev.preventDefault();
              cambiar('q', busqueda.trim());
            }}
          >
            <input
              type="search"
              placeholder="Cliente, código o motivo"
              value={busqueda}
              onChange={(ev) => setBusqueda(ev.target.value)}
              aria-label="Buscar solicitudes"
            />
            <Boton type="submit" variante="secundario">
              Buscar
            </Boton>
          </form>
        </div>

        {error && <Aviso tono="error">{error}</Aviso>}

        <Tarjeta sinRelleno>
          {cargando ? (
            <Cargando />
          ) : datos && datos.rows.length > 0 ? (
            <>
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Solicitud</th>
                    <th>Cliente</th>
                    <th>Comercial</th>
                    <th>Fecha de la visita</th>
                    <th>Situación</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.rows.map((r) => (
                    <tr key={r.id} data-clicable="true" onClick={() => navegar(`/solicitudes/${r.id}`)}>
                      <td>
                        <div className="codigo">{r.publicCode}</div>
                        <div className="sutil">Creada el {fecha(r.createdAt)}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.client.name}</div>
                        <div className="sutil">{r.client.city ?? '—'}</div>
                        <div className="sutil">{tipoVisita(r.visitType)} · {lugarVisita(r.venueType, r.venueOther)}</div>
                      </td>
                      <td className="sutil">{r.owner.name}</td>
                      <td className="dato" style={{ fontSize: 13 }}>
                        {r.finalStart ? franja(r.finalStart, r.finalEnd) : 'Sin fijar'}
                      </td>
                      <td>
                        <Pildora estado={r.status} />{Boolean(r.rescheduleCount)&&<span className="sutil">Reprogramada</span>}
                        <div className="sutil" style={{ marginTop: 4 }}>
                          {turnoDe(r.status, r.kitchenPlanStatus)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {paginas > 1 && (
                <div className="fila" style={{ justifyContent: 'space-between', padding: '12px 24px' }}>
                  <span className="sutil">
                    {total} solicitudes · página {pagina} de {paginas}
                  </span>
                  <div className="fila">
                    <Boton
                      variante="secundario"
                      tam="s"
                      disabled={pagina <= 1}
                      onClick={() => cambiar('pagina', String(pagina - 1))}
                    >
                      Anterior
                    </Boton>
                    <Boton
                      variante="secundario"
                      tam="s"
                      disabled={pagina >= paginas}
                      onClick={() => cambiar('pagina', String(pagina + 1))}
                    >
                      Siguiente
                    </Boton>
                  </div>
                </div>
              )}
            </>
          ) : (
            <Vacio
              titulo="No hay solicitudes con estos filtros"
              accion={
                estado || q ? (
                  <Boton variante="secundario" onClick={() => setParams({}, { replace: true })}>
                    Quitar filtros
                  </Boton>
                ) : es('SALESPERSON', 'ADMIN') ? (
                  <Boton variante="clave" onClick={() => navegar('/solicitudes/nueva')}>
                    Crear la primera
                  </Boton>
                ) : null
              }
            >
              Pruebe con otro estado o con otra búsqueda.
            </Vacio>
          )}
        </Tarjeta>
      </div>
    </>
  );
}
